import { Injectable, computed, inject } from '@angular/core';
import { IndustryType } from '../../shared/models/industry-type.model';
import { getIndustryCatalogProfile } from '../data/industry-catalog.profile';
import { coerceIndustryType } from '../utils/industry.util';
import { UserSessionService } from './user-session.service';

/**
 * Exposes the current tenant industry (from select-tenant → session storage)
 * so features can adapt UI without hardcoding industry checks everywhere.
 */
@Injectable({ providedIn: 'root' })
export class IndustryContextService {
  private readonly session = inject(UserSessionService);

  readonly industry = computed(() => {
    const fromUser = coerceIndustryType(this.session.user()?.industry ?? null);
    if (fromUser != null) {
      return fromUser;
    }
    const tenantId = (this.session.user()?.tenantId ?? '').trim();
    if (!tenantId) {
      return null;
    }
    const match = this.session.tenants().find((t) => t.tenantId === tenantId);
    return coerceIndustryType(match?.industry ?? null);
  });

  /** Catalog UX defaults (variant axes, suggestions) for the active industry. */
  readonly catalogProfile = computed(() => getIndustryCatalogProfile(this.industry()));

  readonly supportsVariants = computed(() => this.catalogProfile().supportsVariants);

  readonly isRetailAndEcommerce = computed(
    () => this.industry() === IndustryType.RetailAndEcommerce,
  );

  readonly isFoodAndBeverage = computed(() => this.industry() === IndustryType.FoodAndBeverage);

  is(type: IndustryType): boolean {
    return this.industry() === type;
  }
}
